/* The Finer Things Market — Noir motion layer.
   Everything here is decoration: if this file fails to load, the store works
   exactly as stock Dawn. Uses event delegation so it survives the theme editor
   re-rendering sections, and never runs a frame loop while the tab is hidden. */
(() => {
  if (window.__finerNoir) return;
  window.__finerNoir = true;

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fine = matchMedia('(pointer: fine)').matches;
  const mobile = matchMedia('(max-width: 749px)').matches || !fine;
  const root = document.documentElement;
  const add = (tag, cls, html) => { const el = document.createElement(tag); el.className = cls; if (html) el.innerHTML = html; document.body.appendChild(el); return el; };

  add('div', 'fn-grain').setAttribute('aria-hidden', 'true');
  // The living backdrop sits under everything (z-index -1); sections are glass over it.
  const backdrop = document.createElement('div');
  backdrop.className = 'fn-backdrop'; backdrop.setAttribute('aria-hidden', 'true');
  backdrop.innerHTML = '<span class="aura a1"></span><span class="aura a2"></span><span class="aura a3"></span><span class="grid"></span>';
  document.body.prepend(backdrop);
  const progress = add('div', 'fn-progress'); progress.setAttribute('aria-hidden', 'true');

  /* ---------- gold dust: a sparse, slow field of light ---------- */
  let dust = null, dctx = null, motes = [], W = 0, H = 0, DPR = 1;
  if (!reduced) {
    dust = document.createElement('canvas'); dust.className = 'fn-dust'; dust.setAttribute('aria-hidden', 'true');
    backdrop.appendChild(dust);
    dctx = dust.getContext('2d');
    const N = mobile ? 36 : 78;
    const size = () => {
      DPR = Math.min(window.devicePixelRatio || 1, 1.5);
      W = innerWidth; H = innerHeight;
      dust.width = Math.round(W * DPR); dust.height = Math.round(H * DPR);
      dctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    };
    size(); addEventListener('resize', size, { passive: true });
    for (let i = 0; i < N; i++) {
      // One in six is a "star" that throws a small four-point glint when it peaks.
      motes.push({ x: Math.random() * W, y: Math.random() * H, r: 0.6 + Math.random() * 1.7, s: 0.08 + Math.random() * 0.3, p: Math.random() * 6.28, a: 0.45 + Math.random() * 0.5, star: Math.random() < 0.17 });
    }
  }

  /* ---------- scroll: progress line, glass header, banner push-in ---------- */
  let scrollY = 0;
  const onScroll = () => {
    scrollY = window.scrollY;
    const max = Math.max(1, document.documentElement.scrollHeight - innerHeight);
    progress.style.setProperty('--fn-p', (scrollY / max).toFixed(4));
    root.classList.toggle('fn-scrolled', scrollY > 30);
  };
  addEventListener('scroll', onScroll, { passive: true }); onScroll();

  /* ---------- cursor ---------- */
  const mouse = { x: -100, y: -100, rx: -100, ry: -100 };
  let cursor = null, cd = null, cr = null;
  if (fine) {
    cursor = add('div', 'fn-cursor', '<i class="d"></i><i class="r"></i>');
    cursor.setAttribute('aria-hidden', 'true');
    cd = cursor.querySelector('.d'); cr = cursor.querySelector('.r');
  }
  addEventListener('pointermove', e => {
    mouse.x = e.clientX; mouse.y = e.clientY;
    if (cursor) cursor.classList.toggle('on', !!e.target.closest('a, button, input, select, textarea, label, summary, [role="button"]'));
  }, { passive: true });

  /* ---------- 3D tilt + light on cards (mouse only) ---------- */
  if (fine && !reduced) {
    let active = null;
    const reset = card => { if (!card) return; card.style.transform = ''; };
    document.addEventListener('pointermove', e => {
      const wrap = e.target.closest && e.target.closest('.card-wrapper');
      const card = wrap && wrap.querySelector('.card');
      if (active && active !== card) { reset(active); active = null; }
      if (!card) return;
      active = card;
      const inner = card.querySelector('.card__inner') || card;
      if (!inner.querySelector(':scope > .fn-glare')) {
        const g = document.createElement('span'); g.className = 'fn-glare'; g.setAttribute('aria-hidden', 'true');
        inner.appendChild(g);
      }
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width, py = (e.clientY - r.top) / r.height;
      card.style.transform = `rotateX(${((0.5 - py) * 7).toFixed(2)}deg) rotateY(${((px - 0.5) * 9).toFixed(2)}deg) translateY(-6px)`;
      inner.style.setProperty('--fn-gx', `${(px * 100).toFixed(1)}%`);
      inner.style.setProperty('--fn-gy', `${(py * 100).toFixed(1)}%`);
    }, { passive: true });
    document.addEventListener('pointerleave', () => { reset(active); active = null; });
  }

  /* ---------- names like "Electronics/Accessories": let them wrap after the slash, not mid-word ---------- */
  const slashBreaks = (scope = document) => {
    scope.querySelectorAll('.card__heading a, .card__heading').forEach(el => {
      if (el.dataset.fnWbr) return; el.dataset.fnWbr = '1';
      const walk = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const nodes = []; while (walk.nextNode()) if (walk.currentNode.nodeValue.includes('/')) nodes.push(walk.currentNode);
      nodes.forEach(n => {
        const parts = n.nodeValue.split('/'); const frag = document.createDocumentFragment();
        parts.forEach((t, i) => { frag.appendChild(document.createTextNode(t + (i < parts.length - 1 ? '/' : ''))); if (i < parts.length - 1) frag.appendChild(document.createElement('wbr')); });
        n.replaceWith(frag);
      });
    });
  };
  slashBreaks();
  document.addEventListener('shopify:section:load', e => slashBreaks(e.target));

  /* ---------- one loop, capped at 60fps, idle when hidden ---------- */
  let last = 0;
  const banners = () => document.querySelectorAll('.banner__media img, .banner__media video');
  const frame = now => {
    requestAnimationFrame(frame);
    if (document.hidden || now - last < 16) return;
    const dt = Math.min(0.05, (now - last) / 1000); last = now;
    if (cursor) {
      mouse.rx += (mouse.x - mouse.rx) * 0.2; mouse.ry += (mouse.y - mouse.ry) * 0.2;
      cd.style.transform = `translate3d(${mouse.x}px, ${mouse.y}px, 0)`;
      cr.style.transform = `translate3d(${mouse.rx}px, ${mouse.ry}px, 0)`;
    }
    if (!reduced) {
      // Banner: slow push-in that settles, plus a little parallax.
      banners().forEach(m => {
        const r = m.getBoundingClientRect();
        if (r.bottom < 0 || r.top > innerHeight) return;
        const k = Math.min(1, (now / 1000) / 9);           // settles over ~9s
        const s = 1.12 - 0.08 * (1 - Math.pow(1 - k, 3));
        const shift = (r.top + r.height / 2 - innerHeight / 2) * -0.06;
        m.style.transform = `translate3d(0, ${shift.toFixed(1)}px, 0) scale(${s.toFixed(4)})`;
      });
    }
    if (dctx) {
      dctx.clearRect(0, 0, W, H);
      dctx.globalCompositeOperation = 'lighter';
      for (const m of motes) {
        m.y -= m.s * 60 * dt; m.x += Math.sin(now / 4000 + m.p) * 0.12;
        if (m.y < -6) { m.y = H + 6; m.x = Math.random() * W; }
        const tw = 0.55 + 0.45 * Math.sin(now / 820 + m.p * 3);
        const a = m.a * tw;
        // soft halo
        dctx.globalAlpha = a * 0.5;
        const g = dctx.createRadialGradient(m.x, m.y, 0, m.x, m.y, m.r * 5);
        g.addColorStop(0, 'rgba(240, 214, 156, 1)'); g.addColorStop(0.35, 'rgba(214, 178, 106, .5)'); g.addColorStop(1, 'rgba(201, 164, 92, 0)');
        dctx.fillStyle = g; dctx.beginPath(); dctx.arc(m.x, m.y, m.r * 5, 0, 6.2832); dctx.fill();
        // bright core
        dctx.globalAlpha = Math.min(1, a * 1.1);
        dctx.fillStyle = 'rgba(255, 244, 214, 1)'; dctx.beginPath(); dctx.arc(m.x, m.y, m.r * 0.55, 0, 6.2832); dctx.fill();
        // glint on the stars, only near the top of their twinkle
        if (m.star && tw > 0.86) {
          const k = (tw - 0.86) / 0.14, L = m.r * (5 + 7 * k);
          dctx.globalAlpha = 0.55 * k * m.a; dctx.strokeStyle = 'rgba(255, 238, 196, 1)'; dctx.lineWidth = 0.8;
          dctx.beginPath(); dctx.moveTo(m.x - L, m.y); dctx.lineTo(m.x + L, m.y); dctx.moveTo(m.x, m.y - L); dctx.lineTo(m.x, m.y + L); dctx.stroke();
        }
      }
      dctx.globalAlpha = 1; dctx.globalCompositeOperation = 'source-over';
    }
  };
  requestAnimationFrame(frame);
})();
